import { useEffect, useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Monitor, Smartphone, Play, Square, Film, Music, ArrowLeftRight, Hand, KeyRound,
} from 'lucide-react'
import { Card, CardContent } from '@meno/ui/components/ui/card'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@meno/ui/components/ui/dialog'
import { toast } from 'sonner'
import {
  getScreenContent, publishTransition, BACKEND_URL,
  type ScreenContentSync, type SyncContentItem, type SyncContentLayout,
} from '@meno/ui/lib/api'
import { formatRelativeSyncTime } from '@meno/ui/lib/utils'
import type { ContentScaling, OrientationType, RotationDegrees } from './editBufferReducer'
import PdfThumbnail from '@/components/PdfThumbnail'
import StopScreenChannel from './StopScreenChannel'
import ChannelCard from './ChannelCard'

// Transition options offered on the screen page's Transitions channel. Keys are
// real catalog keys (see lib/transitions.ts) so picking one publishes it to this
// screen's display_settings and the players apply it. Full catalog + speed live
// on the dedicated Transitions page. Exported so the screens-list info
// drawer's Content tab can resolve the same key → label the same way,
// instead of keeping a second copy that can drift.
export const TRANSITIONS: { key: string; labelKey: string; fallback: string }[] = [
  { key: 'default',    labelKey: 'screens.transitionDefault', fallback: 'Default (Cut)' },
  { key: 'fade',       labelKey: 'screens.transitionFade',    fallback: 'Fade' },
  { key: 'slide-left', labelKey: 'screens.transitionSlide',   fallback: 'Slide' },
  { key: 'zoom-in',    labelKey: 'screens.transitionZoom',    fallback: 'Zoom' },
]

const isVideoFile = (type: string = '', name: string = '') =>
  type.toLowerCase().includes('video') || /\.(mp4|webm|mov|mkv|avi)$/i.test(name)
const isImageFile = (type: string = '', name: string = '') =>
  type.toLowerCase().includes('image') || /\.(jpg|jpeg|png|gif|webp)$/i.test(name)
const isPdfFile = (type: string = '', name: string = '') =>
  type.toLowerCase().includes('pdf') || /\.pdf($|\?)/i.test(name)

type ItemRef     = { id: number; name: string }
type MediaRef    = { id: number; original_name: string; file_type: string; url: string; file_size?: number; created_at?: string }
type PlaylistRef = { id: number; name: string; media: MediaRef[]; media_durations: Record<number, number> }
type ScreenLike  = {
  id: number
  public_id: string
  disconnect_at?: string | null
  screenshot_url?: string
  screenshot_taken_at?: string | null
  name?: string
}

import { Button } from '@meno/ui/components/ui/button'
import RenewalRequestDialog from './RenewalRequestDialog'
// Shown over the preview frame (both playback + sync modes, and the
// screens-list drawer's own SyncPreview) when the screen's license has
// expired — the preview is still rendered underneath (it's an overlay, not
// a replacement), same idea as the fallback-image banners elsewhere: tell
// the operator WHY what they're looking at may not be what's really on the
// physical screen right now.
export function LicenseExpiredOverlay({ t, publicId, screenName, expiresAt }: {
  t: ReturnType<typeof useTranslation>['t']
  /** When given, the overlay offers a renewal request. Explicit scalars rather
   *  than a screen object: SyncPreview only ever receives a public-id string,
   *  and an object prop there tempted a bare `screen` reference that silently
   *  resolved to the DOM's window.screen global instead of failing loudly. */
  publicId?: string
  screenName?: string
  expiresAt?: string | null
}) {
  const [renewalOpen, setRenewalOpen] = useState(false)
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-black/70 text-white text-center px-4">
      <KeyRound className="w-6 h-6 text-destructive" />
      <p className="text-sm font-semibold">{t('screens.expiredTitle', 'License expired')}</p>
      <p className="text-xs text-white/70 max-w-[220px]">{t('screens.expiredBodyShort', 'renew to bring this screen back online.')}</p>
      {/* The action lives HERE rather than in a page-level banner: that banner
          (ExpiredScreenBanner) was deliberately removed from this page, and
          this overlay is now the deliberate expired surface — sitting on the
          preview the operator is already staring at when they find out the
          screen is dark. Until now it told them to renew and gave them no way
          to ask, so the request arrived by email or never. */}
      {publicId && (
        <>
          <Button
            size="sm"
            onClick={() => setRenewalOpen(true)}
            className="mt-1 bg-white text-black hover:bg-white/90"
          >
            {t('screens.renewal.cta', 'Request renewal')}
          </Button>
          <RenewalRequestDialog
            open={renewalOpen}
            onOpenChange={setRenewalOpen}
            publicId={publicId}
            screenName={screenName ?? ''}
            expiresAt={expiresAt}
          />
        </>
      )}
    </div>
  )
}

// ── ScreenPreview ──────────────────────────────────────────────────────────
//
// The "Playback" tab: cycles through what this screen is SUPPOSED to be
// playing, on the operator's command, starting from item 1.
//
// It builds that rotation from the SAME manifest the "Sync" tab reads
// (GET /screens/{id}/content) rather than from the page's local assigned*
// lists. Those lists only ever covered directly-assigned media, menus, and a
// playlist's `media` — so a screen whose content was an interactive link, a
// playlist containing links, or a zoned layout rendered as "No content
// assigned" (a blank frame) while the Sync tab, reading the manifest, showed
// that content perfectly well. That mismatch is the whole bug: two previews
// of one screen disagreeing about whether it has content.
//
// Sharing the manifest means every content type the players understand is
// rendered here identically, by the same `SyncItem` / `SyncLayoutGrid`
// components the Sync tab uses. Playback and Sync now differ in exactly one
// intended way: Sync seats the rotation at the DEVICE's computed playhead,
// Playback starts at item 1 when the operator presses Play.

function ScreenPreview({
  screen, orientationType, rotationDegrees, scaling, expired,
}: {
  screen:            ScreenLike
  orientationType:   OrientationType
  rotationDegrees:   RotationDegrees
  scaling:           ContentScaling
  expired?:          boolean
}) {
  const { t } = useTranslation()
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [data, setData] = useState<ScreenContentSync | null>(null)
  const [loadError, setLoadError] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Anchor for a zoned layout's local rotation — stamped when Play is pressed
  // so each zone starts at its own first item rather than at the device
  // playhead. State, not a ref: the layout grid is a pure function of
  // (startedAt, now), so both have to be render inputs.
  const [startedAt, setStartedAt] = useState<number>(() => Date.now())
  // Re-render once a second while a zoned layout is playing so each zone's
  // rotation actually advances between the 5s manifest polls.
  const [now, setNow] = useState<number>(() => Date.now())

  // Poll on the same cadence as the Sync tab so a publish made elsewhere on
  // the page shows up here without a reload, and so the two tabs can never be
  // looking at different manifests.
  useEffect(() => {
    let cancelled = false
    let handle: number | null = null
    const fetchNow = () => {
      getScreenContent(screen.public_id)
        .then(r => { if (!cancelled) { setData(r.data); setLoadError(false) } })
        .catch(() => { if (!cancelled) setLoadError(true) })
    }
    fetchNow()
    handle = window.setInterval(fetchNow, SYNC_POLL_MS)
    return () => {
      cancelled = true
      if (handle !== null) window.clearInterval(handle)
    }
  }, [screen.public_id])

  const items     = data?.content.items ?? []
  const layout    = data?.content.layout ?? null
  const hasLayout = !!layout && layout.zones.length > 0

  const advance = useCallback(() => {
    setCurrentIndex(i => (i + 1) % Math.max(items.length, 1))
  }, [items.length])

  const currentItem     = items.length > 0 ? items[currentIndex % items.length] : null
  const currentDuration = currentItem?.duration ?? 10
  // Videos self-advance on `ended`; everything else runs on a duration timer.
  const currentIsVideo  = !!currentItem?.url && isVideoFile('', currentItem.url)

  useEffect(() => {
    if (!isPlaying || items.length === 0 || hasLayout) return
    if (currentIsVideo) return
    timerRef.current = setTimeout(advance, Math.max(1, currentDuration) * 1000)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [currentIndex, advance, items.length, isPlaying, hasLayout, currentIsVideo, currentDuration])

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [])

  // Zoned layouts run their own per-zone rotations off the (startedAt, now)
  // pair rather than a single currentIndex, so they need a clock rather than
  // an advance timer.
  useEffect(() => {
    if (!isPlaying || !hasLayout) return
    const h = window.setInterval(() => setNow(Date.now()), 1_000)
    return () => window.clearInterval(h)
  }, [isPlaying, hasLayout])

  // Play runs until the operator stops it.
  //
  // It used to arm a 5s timer that forced isPlaying back to false. Items
  // default to 10s, so the preview never reached its first advance: you got
  // one frame for five seconds, no rotation, then the Play button again — and
  // with no stop control that five-second flash was the whole feature. A video
  // was cut off mid-play for the same reason. Rotating through the assigned
  // content is the entire point of playback mode, so there is nothing here for
  // a timeout to protect against.
  const startPreview = () => {
    const at = Date.now()
    setStartedAt(at)
    setNow(at)
    setCurrentIndex(0)
    setIsPlaying(true)
  }

  // Stop returns to the start rather than freezing mid-rotation, so pressing
  // Play again always shows the same thing — item 1 — instead of resuming
  // wherever the last run happened to be.
  const stopPreview = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setIsPlaying(false)
    setCurrentIndex(0)
  }

  // Frame aspect ratio comes from orientation type (16:9 for horizontal,
  // 9:16 for vertical). The rotation degrees rotate the inner content stage
  // around centre — the outer frame keeps its natural aspect so the operator
  // can see the upside-down / sideways render the physical device produces.
  const isHorizontal = orientationType === 'horizontal'
  // Raised from the old 720px/52vh (horizontal) and 360px/60vh (vertical)
  // ceilings — on a normal-height browser window those caps engaged well
  // before the 100% container width did, so the mockup stayed noticeably
  // smaller than the panel actually had room for. Still bounded by `100%`
  // so it never overflows a narrow panel, just allowed to grow much bigger
  // on a panel that has the room.
  // Horizontal was pushed further still to 1500px/88vh, which a 16:9 frame
  // absorbs fine — it is wide, not tall.
  // VERTICAL stays at 520px/80vh. The same raise took it to 680px/92vh, and
  // at 92vh a 9:16 mockup plus the panel's own header and controls is taller
  // than the viewport, so the preview panel started scrolling — on the one
  // surface an operator is trying to eyeball as a whole. The parent container
  // is deliberately left as-is; only the inner frame is capped back.
  const frameW = isHorizontal
    ? 'min(100%, 1500px, calc(88vh * 16 / 9))'
    : 'min(88%, 520px, calc(80vh * 9 / 16))'
  const frameAspect = isHorizontal ? '16/9' : '9/16'

  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <div
          style={{ width: frameW, aspectRatio: frameAspect, transform: 'translateZ(0)' }}
          className="relative rounded-xl overflow-hidden border-2 border-border/60 bg-zinc-950 shadow-2xl"
        >
        {expired && (
          <LicenseExpiredOverlay
            t={t} publicId={screen.public_id}
            screenName={screen.name} expiresAt={screen.disconnect_at}
          />
        )}
        <div
          className="absolute inset-0"
          style={{ transform: rotationDegrees ? `rotate(${rotationDegrees}deg)` : undefined, transformOrigin: 'center center' }}
        >
          {isPlaying && (
            hasLayout ? (
              // Zoned layout — the same grid the Sync tab and the players
              // draw, anchored at the moment Play was pressed so each zone
              // rotates from its own first item.
              <SyncLayoutGrid layout={layout!} startedAt={startedAt} now={now} t={t} scaling={scaling} />
            ) : items.length === 0 ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/30">
                <Monitor className="w-10 h-10" />
                <p className="text-sm">
                  {loadError
                    ? t('screens.preview.syncError', 'Could not load sync data.')
                    : t('screens.noContent', 'No content assigned')}
                </p>
              </div>
            ) : (
              // One renderer for every content type — media, PDF, menu, and
              // interactive link — shared with the Sync tab.
              <SyncItem
                key={currentItem ? currentItem.type + '-' + currentItem.id + '-' + currentIndex : 'empty'}
                item={currentItem}
                offsetSec={0}
                t={t}
                loopVideo={false}
                onVideoEnded={advance}
                scaling={scaling}
              />
            )
          )}

          {!isPlaying && <div className="absolute inset-0 bg-zinc-900" />}

          {!isPlaying && (
            <button
              onClick={startPreview}
              className="absolute inset-0 flex items-center justify-center group"
            >
              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-colors shadow-lg">
                <Play className="w-7 h-7 text-white fill-white ms-1" />
              </div>
            </button>
          )}

          {isPlaying && !hasLayout && currentItem && !currentIsVideo && (
            <ProgressBar key={currentIndex + '-' + currentItem.id} durationSec={currentDuration} />
          )}

          {isPlaying && !hasLayout && items.length > 1 && (
            <div className="absolute top-2 end-2 bg-black/60 text-white/70 text-[10px] px-2 py-0.5 rounded-full">
              {(currentIndex % items.length) + 1}/{items.length}
            </div>
          )}

          {/* Stop. Previously there was none — playback ended on its own after
              5s, so nothing needed one. Now that it runs until told otherwise,
              the operator needs a way out that is not "navigate away". Small
              and corner-anchored so it never covers the content being judged. */}
          {isPlaying && (
            <button
              onClick={stopPreview}
              title={t('screens.preview.stop', 'Stop preview')}
              aria-label={t('screens.preview.stop', 'Stop preview')}
              className="absolute top-2 start-2 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 text-white/80 hover:text-white flex items-center justify-center transition-colors"
            >
              <Square className="w-3 h-3 fill-current" />
            </button>
          )}
        </div>

        {/* Last capture, inset into the playback frame. The preview above
            shows what the schedule SHOULD be putting on this screen; the
            screenshot is what the device actually last rendered. Side by side
            the two answer "is it really showing that?" without scrolling down
            to the full-size card (which stays below — this is an addition, not
            a replacement).

            Deliberately a sibling of the rotated stage, not a child: the stage
            carries the screen's rotation transform, and a thumbnail that
            rotated with it would be upside-down on a 180° screen — the one
            case where you most want a readable reference image. */}
        {screen.screenshot_url && isPlaying && (
          <div className="absolute bottom-2 start-2 w-1/4 max-w-[132px] min-w-[76px] pointer-events-none">
            <div className="rounded-md overflow-hidden border border-white/25 shadow-lg bg-black/60 backdrop-blur-sm">
              <img
                src={screen.screenshot_url}
                alt={t('screens.lastScreenshot', '{{name}} — Last Screenshot', { name: screen.name ?? '' })}
                className="block w-full object-contain"
              />
              <p className="px-1.5 py-1 text-[9px] leading-tight text-white/70 truncate">
                {screen.screenshot_taken_at
                  ? t('screens.lastCaptureAt', 'Last capture · {{when}}', { when: formatRelativeSyncTime(screen.screenshot_taken_at) })
                  : t('screens.lastCapture', 'Last capture')}
              </p>
            </div>
          </div>
        )}
        </div>
      </div>

      {screen.screenshot_url && (
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('screens.lastScreenshot', '{{name}} — Last Screenshot', { name: screen.name ?? '' })}</p>
              {screen.screenshot_taken_at && (
                <p className="text-[11px] text-muted-foreground shrink-0">
                  {t('screens.screenshotTakenAt', 'Captured {{when}}', { when: formatRelativeSyncTime(screen.screenshot_taken_at) })}
                </p>
              )}
            </div>
            <div className="rounded-lg overflow-hidden border border-border">
              <img src={screen.screenshot_url} alt={t('screens.lastScreenshot', '{{name}} — Last Screenshot', { name: screen.name ?? '' })} className="w-full object-contain max-h-72" />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function ProgressBar({ durationSec }: { durationSec: number }) {
  const [pct, setPct] = useState(0)
  useEffect(() => {
    const start = Date.now()
    const total = durationSec * 1000
    const raf = { id: 0 }
    const tick = () => {
      const elapsed = Date.now() - start
      setPct(Math.min(100, (elapsed / total) * 100))
      if (elapsed < total) raf.id = requestAnimationFrame(tick)
    }
    raf.id = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf.id)
  }, [durationSec])
  return (
    <div className="absolute bottom-0 start-0 end-0 h-1 bg-black/30">
      <div className="h-full bg-primary transition-none" style={{ width: `${pct}%` }} />
    </div>
  )
}

// ── SyncPreview ────────────────────────────────────────────────────────────
// Computes which item the *device* should be playing right now based on the
// rotation start anchor (last_seen) + item durations, then renders that item
// at the right offset so the operator can compare against the physical screen.
//
// Refetches every SYNC_POLL_MS so it picks up content/orientation changes;
// pauses when the tab is inactive (visibilityState).
//
// Tradeoff: this is *computed*, not observed. The device's actual playhead
// may drift by up to ~30s from this estimate (heartbeat interval). For
// frame-accurate sync the device would need to report currently_playing_id.

const SYNC_POLL_MS = 5_000

// Exported so the screens-list info drawer's Content tab can render the exact
// same live/synced preview panel instead of reimplementing it — see
// ScreenInfoDrawer.tsx's Content tab.
export function SyncPreview({
  screenId, orientationType, rotationDegrees, t, expired, scaling = 'default',
}: {
  screenId:        string
  orientationType: OrientationType
  rotationDegrees: RotationDegrees
  t:               ReturnType<typeof useTranslation>['t']
  /** Screen's license has expired — overlays a notice on top of the (still
   *  rendered underneath) preview, same as ScreenDetailsPage's PreviewColumn. */
  expired?:        boolean
  scaling?:        ContentScaling
}) {
  const [data,  setData]  = useState<ScreenContentSync | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Bumped every second so the current-item index recomputes without
  // refetching from the server (refetch happens on the poll interval).
  const [tick,  setTick]  = useState(0)

  useEffect(() => {
    let cancelled = false
    let pollHandle: number | null = null
    const fetchNow = () => {
      getScreenContent(screenId)
        .then(r => { if (!cancelled) { setData(r.data); setError(null) } })
        .catch(() => { if (!cancelled) setError(t('screens.preview.syncError', 'Could not load sync data.')) })
    }
    const tickHandle = window.setInterval(() => setTick(n => n + 1), 1_000)
    const startPoll = () => {
      fetchNow()
      pollHandle = window.setInterval(fetchNow, SYNC_POLL_MS)
    }
    const stopPoll = () => {
      if (pollHandle !== null) { window.clearInterval(pollHandle); pollHandle = null }
    }
    const onVis = () => {
      if (document.visibilityState === 'visible') startPoll()
      else stopPoll()
    }
    document.addEventListener('visibilitychange', onVis)
    if (document.visibilityState === 'visible') startPoll()
    return () => {
      cancelled = true
      stopPoll()
      window.clearInterval(tickHandle)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [screenId, t])

  // Reference `tick` so the linter knows the render depends on it.
  void tick

  if (error) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive text-center">
        {error}
      </div>
    )
  }

  if (!data) {
    return (
      <div className="rounded-xl border border-border bg-muted/10 p-10 text-center text-sm text-muted-foreground">
        {t('common.loading', 'Loading…')}
      </div>
    )
  }

  const items  = data.content.items
  const layout = data.content.layout
  const isHorizontal = orientationType === 'horizontal'
  // Raised from the old 720px/52vh (horizontal) and 360px/60vh (vertical)
  // ceilings — on a normal-height browser window those caps engaged well
  // before the 100% container width did, so the mockup stayed noticeably
  // smaller than the panel actually had room for. Still bounded by `100%`
  // so it never overflows a narrow panel, just allowed to grow much bigger
  // on a panel that has the room.
  // Horizontal was pushed further still to 1500px/88vh, which a 16:9 frame
  // absorbs fine — it is wide, not tall.
  // VERTICAL stays at 520px/80vh. The same raise took it to 680px/92vh, and
  // at 92vh a 9:16 mockup plus the panel's own header and controls is taller
  // than the viewport, so the preview panel started scrolling — on the one
  // surface an operator is trying to eyeball as a whole. The parent container
  // is deliberately left as-is; only the inner frame is capped back.
  const frameW = isHorizontal
    ? 'min(100%, 1500px, calc(88vh * 16 / 9))'
    : 'min(88%, 520px, calc(80vh * 9 / 16))'
  const frameAspect = isHorizontal ? '16/9' : '9/16'

  const startedAt = data.last_seen ? new Date(data.last_seen).getTime() : new Date(data.server_now).getTime()
  const now       = Date.now()
  // Flat rotation (no layout): pick the single item the device should be on.
  const flat = pickCurrentSyncItem(items, startedAt, now)

  return (
    <div className="space-y-2">
      <div className="flex justify-center">
        <div
          style={{ width: frameW, aspectRatio: frameAspect }}
          className="relative rounded-xl overflow-hidden border-2 border-border/60 bg-zinc-950 shadow-2xl"
        >
          {expired && <LicenseExpiredOverlay t={t} publicId={screenId} />}
          <div
            className="absolute inset-0"
            style={{ transform: rotationDegrees ? `rotate(${rotationDegrees}deg)` : undefined, transformOrigin: 'center center' }}
          >
            {layout && layout.zones.length > 0 ? (
              // Zoned layout — render the split grid, each zone showing the
              // item its own rotation should be on right now. Mirrors the
              // player's LayoutDisplay so the operator sees the real split.
              <SyncLayoutGrid layout={layout} startedAt={startedAt} now={now} t={t} scaling={scaling} />
            ) : (
              <SyncItem item={flat.current} offsetSec={flat.offsetSec} t={t} scaling={scaling} />
            )}
          </div>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground text-center">
        {data.last_seen
          ? t('screens.preview.syncVerified', { defaultValue: 'Synced from last_seen {{when}}', when: new Date(data.last_seen).toLocaleTimeString() })
          : t('screens.preview.syncNoLastSeen', 'Device has not checked in yet — showing first item.')}
      </p>
    </div>
  )
}

// Pick the item a rotation should be on right now, plus the in-item offset.
// Shared by the flat preview, each zone of a layout preview, and the
// screens-list info drawer's computed preview.
export function pickCurrentSyncItem(
  items: SyncContentItem[], startedAt: number, now: number,
): { current: SyncContentItem | null; offsetSec: number } {
  if (items.length === 0) return { current: null, offsetSec: 0 }
  const total = items.reduce((sum, i) => sum + (i.duration ?? 10), 0) || 1
  const elapsed = ((now - startedAt) / 1000) % total
  let acc = 0
  for (let i = 0; i < items.length; i++) {
    const dur = items[i].duration ?? 10
    if (elapsed < acc + dur) return { current: items[i], offsetSec: elapsed - acc }
    acc += dur
  }
  return { current: items[0], offsetSec: 0 }
}

// One rendered sync item (menu iframe / video seeked to offset / image / text).
// `item === null` renders the empty-content hint. Also consumed by the
// screens-list info drawer's preview fallback.
export function SyncItem({
  item, offsetSec, t, loopVideo = true, onVideoEnded, scaling = 'default',
}: {
  item: SyncContentItem | null
  offsetSec: number
  t: ReturnType<typeof useTranslation>['t']
  /** Sync mode loops a video forever (it only ever shows the device's current
   *  playhead). Playback mode passes false + `onVideoEnded` so a video plays
   *  once and hands the rotation on, like the players do. */
  loopVideo?: boolean
  onVideoEnded?: () => void
  scaling?: ContentScaling
}) {
  const resolve = (u?: string | null) =>
    !u ? '' : (u.startsWith('http') ? u : `${BACKEND_URL}${u}`)
  if (!item) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-white/40 text-sm">
        {t('screens.noContent', 'No content assigned')}
      </div>
    )
  }
  // Menus AND interactive links both arrive as type 'menu' with a render_url
  // (links carry link_url too and the render endpoint 302s to the target), so
  // one iframe covers both — the same dispatch the players use in
  // HtmlFrameDisplay. An external link gets the tighter sandbox for the same
  // reason the player does: it is third-party content.
  if (item.type === 'menu' && item.render_url) {
    const isLink = !!item.is_interactive
    return (
      <iframe
        key={item.id}
        src={resolve(item.render_url)}
        title={item.name}
        sandbox={isLink
          ? 'allow-scripts allow-forms allow-popups'
          : 'allow-scripts allow-same-origin allow-forms allow-popups'}
        referrerPolicy="strict-origin-when-cross-origin"
        className="absolute inset-0 w-full h-full border-none bg-black"
      />
    )
  }
  if (item.url && isVideoFile('', item.url)) {
    return (
      <video
        key={item.id}
        src={resolve(item.url)}
        autoPlay
        muted
        loop={loopVideo}
        playsInline
        onLoadedMetadata={e => {
          // Seek to the device's computed offset so the dashboard preview
          // lines up with the physical screen's playhead.
          try { e.currentTarget.currentTime = offsetSec } catch { /* ignore */ }
        }}
        onEnded={onVideoEnded}
        // A decode failure must not strand the rotation on a black frame —
        // treat it the same as "finished" so the next item gets its turn.
        onError={onVideoEnded}
        className={`absolute inset-0 w-full h-full ${scaling === 'fit' ? 'object-cover' : 'object-contain'}`}
      />
    )
  }
  if (item.url && isImageFile('', item.url)) {
    return (
      <img
        key={item.id}
        src={resolve(item.url)}
        alt={item.name}
        className={`absolute inset-0 w-full h-full ${scaling === 'fit' ? 'object-cover' : 'object-contain'}`}
      />
    )
  }
  // Raw PDF document — preview its first page (the players page through the
  // rest on the physical screen) instead of showing a bare filename.
  if (item.url && isPdfFile('', item.url)) {
    return (
      <PdfThumbnail
        key={item.id}
        url={resolve(item.url)}
        targetWidth={960}
        fit={scaling === 'fit' ? 'cover' : 'contain'}
        className="absolute inset-0 w-full h-full bg-black"
      />
    )
  }
  return (
    <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm">
      {item.name}
    </div>
  )
}

// Zoned-layout sync grid — mirrors the player's LayoutDisplay. Each zone is
// seated by its grid placement and shows its own current item. Empty zones
// stay blank rather than repeating the "no content" hint per cell.
export function SyncLayoutGrid({
  layout, startedAt, now, t, scaling = 'default',
}: {
  layout: SyncContentLayout
  startedAt: number
  now: number
  t: ReturnType<typeof useTranslation>['t']
  scaling?: ContentScaling
}) {
  const cols = layout.column_weights.length
    ? layout.column_weights
    : Array.from({ length: Math.max(1, layout.cols) }, () => 1)
  const rows = layout.row_weights.length
    ? layout.row_weights
    : Array.from({ length: Math.max(1, layout.rows) }, () => 1)
  return (
    <div
      className="absolute inset-0 bg-black"
      style={{
        display: 'grid',
        gridTemplateColumns: cols.map(w => `${Math.max(0.05, w)}fr`).join(' '),
        gridTemplateRows:    rows.map(w => `${Math.max(0.05, w)}fr`).join(' '),
      }}
    >
      {layout.zones.map(zone => {
        const { current, offsetSec } = pickCurrentSyncItem(zone.items, startedAt, now)
        return (
          <div
            key={zone.id}
            className="relative overflow-hidden bg-black"
            style={{
              gridColumn: `${zone.col} / span ${Math.max(1, zone.col_span)}`,
              gridRow:    `${zone.row} / span ${Math.max(1, zone.row_span)}`,
            }}
          >
            {current && <SyncItem item={current} offsetSec={offsetSec} t={t} scaling={scaling} />}
          </div>
        )
      })}
    </div>
  )
}

// ── PreviewColumn (exported) ───────────────────────────────────────────────

// Both previews now read the screen's content manifest directly (see
// ScreenPreview), so this column no longer needs the page's local assigned*
// lists — they were the source of the playback/sync mismatch. The props are
// kept in the interface as optional so the page can keep passing them without
// a lockstep change, but nothing here consumes them.
export interface PreviewColumnProps {
  screen:              ScreenLike
  orientationType:     OrientationType
  setOrientationType:  (v: OrientationType) => void
  rotationDegrees:     RotationDegrees
  setRotationDegrees:  (v: RotationDegrees) => void
  scaling:             ContentScaling
  /** @deprecated unused — the preview reads the content manifest instead. */
  assignedMedia?:      MediaRef[]
  /** @deprecated unused — the preview reads the content manifest instead. */
  assignedMenus?:      ItemRef[]
  /** @deprecated unused — the preview reads the content manifest instead. */
  assignedPlaylists?:  PlaylistRef[]
  /** @deprecated unused — the preview reads the content manifest instead. */
  mediaDurations?:     Record<number, number>
  /** @deprecated unused — the preview reads the content manifest instead. */
  menuDurations?:      Record<number, number>
  previewMode:         'playback' | 'sync'
  setPreviewMode:      (v: 'playback' | 'sync') => void
  onOpenPicker:        () => void
  // Audio channel — opens an audio-only picker to change the streamed track.
  onOpenAudioPicker:   () => void
  // Clear the content / audio channels (the remove-X next to Change).
  onRemoveContent?:    () => void
  onRemoveAudio?:      () => void
  // Name of the audio currently streaming to this screen, if any.
  audioLabel?:         string | null
  // Name of the content channel's live occupant (playlist / media / menu),
  // resolved server-side from the channel program. Null = channel empty.
  contentLabel?:       string | null
  /** Screen's license has expired — overlays a notice on the preview frame
   *  (playback or sync, whichever is active) so an operator doesn't mistake
   *  a stale/frozen preview for what's actually on the physical screen. */
  expired?:            boolean
}

export default function PreviewColumn({
  screen, orientationType, setOrientationType, rotationDegrees, scaling,
  previewMode, setPreviewMode, onOpenPicker, onOpenAudioPicker,
  onRemoveContent, onRemoveAudio, audioLabel, contentLabel, expired,
}: PreviewColumnProps) {
  const { t } = useTranslation()
  // The Screen Content card's summary comes from `contentLabel` (the channel's
  // live occupant, resolved server-side) — the local assigned* lists now only
  // feed the playback preview below.
  // The 2-way toggle in this column mirrors the Orientation card in the
  // Settings tab — it lets the operator flip between 16:9 and 9:16 content
  // without leaving the preview. Rotation lives in Settings (4 options),
  // since the right place to fine-tune mount direction is alongside the
  // other display knobs.
  const isHorizontal = orientationType === 'horizontal'

  // The tab row selects which single channel row shows below it (`activeTab`).
  // Each row is collapsed to its "now playing" summary; its "Change" button
  // opens that channel's modal (never an inline expansion).
  type ChannelKey = 'stop' | 'media' | 'audio' | 'transitions'
  const [activeTab, setActiveTab] = useState<ChannelKey>('media')
  const [transitionsOpen, setTransitionsOpen] = useState(false)
  const selectTab = (c: ChannelKey) => setActiveTab(c)

  const [transition, setTransitionState] = useState<string>(
    () => (screen as { display_settings?: { transition?: string } }).display_settings?.transition || 'default',
  )
  const setTransition = async (k: string) => {
    setTransitionState(k)
    try {
      await publishTransition({ key: k, screen_ids: [screen.id] })
      toast.success(t('screens.transitionSaved', 'Transition applied to this screen'))
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('common.error', 'Something went wrong'))
    }
  }
  const transitionMeta = TRANSITIONS.find(o => o.key === transition) ?? TRANSITIONS[0]

  // The channel tabs: one row of buttons; the active one's card shows below.
  const CHANNEL_TABS: { key: ChannelKey; icon: React.ElementType; title: string }[] = [
    { key: 'stop',        icon: Hand,           title: t('screens.stopScreen', 'Stop Screen') },
    { key: 'media',       icon: Film,           title: t('screens.screenContent', 'Screen Content') },
    { key: 'audio',       icon: Music,          title: t('screens.audioTrack', 'Audio Track') },
    { key: 'transitions', icon: ArrowLeftRight, title: t('screens.transitions', 'Transitions') },
  ]

  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold">{t('screens.previewMode', 'Preview Mode')}</h3>

      <div className="inline-flex rounded-md border border-border bg-muted/30 p-0.5">
        <button
          type="button"
          onClick={() => setPreviewMode('playback')}
          className={`px-4 py-1.5 text-xs font-medium rounded-[5px] transition-colors ${previewMode === 'playback' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
        >
          {t('screens.preview.playback', 'Playback')}
        </button>
        <button
          type="button"
          onClick={() => setPreviewMode('sync')}
          className={`px-4 py-1.5 text-xs font-medium rounded-[5px] transition-colors ${previewMode === 'sync' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
        >
          {t('screens.preview.sync', 'Sync')}
        </button>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed">
        {previewMode === 'playback'
          ? t('screens.preview.playbackHint', 'Cycles through assigned content to show what the screen is supposed to display.')
          : t('screens.preview.syncHint', 'Shows what the screen is currently displaying so you can verify the live state.')}
      </p>

      {/* Orientation — sits directly under the preview-mode toggle so the
          operator can flip 16:9 / 9:16 framing right where they see it. */}
      <div className="flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {t('screens.orientation', 'Orientation')}
        </span>
        <div className="inline-flex rounded-md border border-border bg-muted/30 p-0.5">
          <button
            type="button"
            onClick={() => setOrientationType('horizontal')}
            aria-pressed={isHorizontal}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-[5px] transition-colors ${
              isHorizontal ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Monitor className="w-3.5 h-3.5" />
            {t('screens.orientHorizontal', 'Horizontal')}
          </button>
          <button
            type="button"
            onClick={() => setOrientationType('vertical')}
            aria-pressed={!isHorizontal}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-[5px] transition-colors ${
              !isHorizontal ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Smartphone className="w-3.5 h-3.5" />
            {t('screens.orientVertical', 'Vertical')}
          </button>
        </div>
      </div>

      {previewMode === 'playback' ? (
        <ScreenPreview
          screen={screen}
          orientationType={orientationType}
          rotationDegrees={rotationDegrees}
          scaling={scaling}
          expired={expired}
        />
      ) : (
        <SyncPreview
          screenId={screen.public_id}
          orientationType={orientationType}
          rotationDegrees={rotationDegrees}
          scaling={scaling}
          t={t}
          expired={expired}
        />
      )}

      {/* Channel tabs — one row of buttons (Stop Screen / Screen Content /
          Audio Track / Transitions). Selecting a tab shows only THAT channel's
          card below it. The card is a collapsible whose header is the "now
          playing" summary and whose body reveals the full details. */}
      <div className="pt-1 space-y-2">
        <div className="grid grid-cols-4 gap-0.5 rounded-lg border border-border bg-muted/30 p-1">
          {CHANNEL_TABS.map(tab => {
            const active = activeTab === tab.key
            const Icon = tab.icon
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => selectTab(tab.key)}
                aria-pressed={active}
                className={`flex flex-col items-center justify-center gap-1 rounded-md px-2 py-2 text-center transition-colors ${
                  active
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-[11px] font-medium leading-tight">{tab.title}</span>
              </button>
            )
          })}
        </div>

        {/* Only the active tab's row is shown. Each row opens a modal. */}
        {activeTab === 'stop' && (
          /* Stop Screen — interactive attract overlay LAYER; non-destructive. */
          <StopScreenChannel screenId={screen.id} />
        )}

        {activeTab === 'media' && (
          <ChannelCard
            icon={Film}
            title={t('screens.screenContent', 'Screen Content')}
            summary={contentLabel ?? t('screens.noContent', 'No content')}
            changeLabel={contentLabel ? t('common.change', 'Change') : t('screens.addContent', 'Add')}
            onChange={onOpenPicker}
            onRemove={contentLabel ? onRemoveContent : undefined}
          />
        )}

        {activeTab === 'audio' && (
          <ChannelCard
            icon={Music}
            title={t('screens.audioTrack', 'Audio Track')}
            summary={audioLabel || t('screens.noAudio', 'No audio')}
            changeLabel={audioLabel ? t('common.change', 'Change') : t('audioHub.stream', 'Stream')}
            onChange={onOpenAudioPicker}
            onRemove={audioLabel ? onRemoveAudio : undefined}
          />
        )}

        {activeTab === 'transitions' && (
          <ChannelCard
            icon={ArrowLeftRight}
            title={t('screens.transitions', 'Transitions')}
            summary={t(transitionMeta.labelKey, transitionMeta.fallback)}
            changeLabel={t('common.change', 'Change')}
            onChange={() => setTransitionsOpen(true)}
            // A transition always has a value, so "remove" resets to Default.
            onRemove={transition !== 'default' ? () => setTransition('default') : undefined}
          />
        )}
      </div>

      {/* Transitions modal — picks how content changes between items. Saved
          per-screen in localStorage (front-end preference; players don't
          consume it yet). */}
      <Dialog open={transitionsOpen} onOpenChange={setTransitionsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('screens.transitions', 'Transitions')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {TRANSITIONS.map(opt => {
                const active = opt.key === transition
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setTransition(opt.key)}
                    aria-pressed={active}
                    className={`px-2 py-2 rounded-md text-sm font-medium border transition-colors ${
                      active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-foreground hover:border-primary/40'
                    }`}
                  >
                    {t(opt.labelKey, opt.fallback)}
                  </button>
                )
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('screens.transitionsHint', 'How content changes between items. Saved for this screen.')}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}


